import { utils } from '@hh/global.js'

const { debounce } = utils

class HHFiltersWrapper extends HTMLElement {
  constructor() {
    super()
    this.filterData = []
    this.desktopMatchQuery = window.matchMedia('(min-width: 990px)')

    // Define all selectors as constants
    this.selectors = {
      // Form elements
      form: 'form',
      filters: 'details.js-filter',
      sort: 'details.js-sort',

      // Filter specific
      filterSort: '.filter--sort',
      filter1: '.js-filter1',
      filter2: '.js-filter2',
      filter3: '.js-filter3',
      filtersContainer: '.collection-filters__filters',

      // Product grid
      productGrid: '#CollectionProductGrid',
      productGridInner: '.js-product-grid',

      // Visual filter elements
      visualFilterTitle: '.visual-filter__title',
      visualFilterBreadcrumbs: '.visual-filter__breadcrumbs--current-item',

      // Active facets
      activeFacets: '.js-facets',
      facetRemove: '.js-facet-remove',

      // Form inputs
      filterCheckbox: '.js-filter-checkbox',
      sortCheckbox: '.js-sort-checkbox',

      // Dropdown components
      dropdownDisclosure: 'dropdown-disclosure',

      // Count bubbles
      bubble: '.js-bubble',
      selected: '.js-selected',
    }

    // Form IDs
    this.formIds = {
      desktop: '#CollectionFiltersForm',
      mobile: '#CollectionFiltersFormMobile',
    }

    // Bind methods
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)
    this.closeDetails = this.closeDetails.bind(this)
    this.onHistoryChange = this.onHistoryChange.bind(this)
  }

  connectedCallback() {
    this.initializeElements()
    this.setupEventListeners()
  }

  initializeElements() {
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 1000)

    // Cache DOM elements
    this.form = this.querySelector(this.selectors.form)
    this.filters = this.querySelectorAll(this.selectors.filters)
    this.sort = this.querySelector(this.selectors.sort)
  }

  setupEventListeners() {
    // Form submission
    this.form?.addEventListener('input', this.debouncedOnSubmit.bind(this))

    // Document level events
    document.addEventListener('focusout', this.handleFocusOut.bind(this))
    document.addEventListener('click', this.handleBodyClick.bind(this))
    window.addEventListener('popstate', this.onHistoryChange)
    window.addEventListener(
      'navigation:change',
      this.handleVisualFilterChange.bind(this)
    )

    // Filter events
    this.setupFilterEvents()

    // Input events
    this.bindActiveFacetButtonEvents()
    this.checkInputOnEnter()
    this.checkInputOnEnterSort()
  }

  setupFilterEvents() {
    this.filters.forEach((filterMenu) => {
      filterMenu.addEventListener('toggle', this.handleFilterToggle.bind(this))
    })

    if (this.sort) {
      this.sort.addEventListener('toggle', this.handleSortToggle.bind(this))
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget?.className === 'js-anchor') {
      this.closeFiltersBySelector([
        this.selectors.filterSort,
        this.selectors.filter1,
        this.selectors.filter2,
        this.selectors.filter3,
      ])
    }
  }

  closeFiltersBySelector(selectors) {
    selectors.forEach((selector) => {
      document.querySelector(selector)?.classList.remove('is-open')
    })
  }

  handleFilterToggle(event) {
    const isOpen = event.target.open === true
    if (!isOpen) return
    this.filters.forEach((filter) => {
      if (filter !== event.target && filter.open) {
        this.closeDetails(filter)
      }
    })

    if (this.desktopMatchQuery.matches) {
      return
    }

    if (!!this.sort && event.target.open) {
      this.closeDetails(this.sort)
    }
  }

  handleSortToggle(event) {
    if (this.desktopMatchQuery.matches || event.target.open === false) {
      return
    }

    this.filters.forEach((filter) => {
      this.closeDetails(filter)
    })
  }

  closeDetails(details) {
    details.classList.remove('is-open')
    details.parentElement.closeAnimation(details)
  }

  handleBodyClick(event) {
    let clickIsInFilter = false
    this.filters.forEach((filter) => {
      if (filter.contains(event.target) || event.target == filter) {
        clickIsInFilter = true
        return
      }
    })
    const clickIsInSort =
      this.sort.contains(event.target) || event.target === this.sort
    if (clickIsInFilter || clickIsInSort) return

    this.filters.forEach((filter) => this.closeDetails(filter))
    this.closeDetails(this.sort)
  }

  onSubmitHandler(event) {
    event.preventDefault()
    const formData = new FormData(event.target.closest(this.selectors.form))
    const searchParams = new URLSearchParams(formData).toString()

    this.querySelectorAll(this.selectors.dropdownDisclosure).forEach(
      (dropdownDisclosure) => {
        this.closeDetails(dropdownDisclosure.querySelector('details'))
      }
    )

    this.renderPage(searchParams, event)
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    this.toggleActiveFacets()
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') + 1
          )
    this.renderPage(url)
  }

  handleVisualFilterChange(event) {
    event.preventDefault()

    const collectionHandle = event.detail.link.split('/').pop()
    const newUrl = `/collections/${collectionHandle}`
    const newState = { collectionHandle }
    history.pushState(newState, '', newUrl)

    // Use Section Rendering API to fetch the new collection's filters
    this.renderPageForVisualFilter(newUrl, event)

    if (event.detail.isTitle) {
      this.updateVisualFilterTitle(event.detail.title)
    }
  }

  updateVisualFilterTitle(title) {
    const titleMarkup = document.querySelector(this.selectors.visualFilterTitle)
    if (titleMarkup) {
      titleMarkup.innerHTML = title
    }

    const breadcrumbMarkup = document.querySelector(
      this.selectors.visualFilterBreadcrumbs
    )
    if (breadcrumbMarkup) {
      breadcrumbMarkup.innerHTML = title
    }
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || ''
    this.renderPage(searchParams, null, false)
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll(this.selectors.facetRemove).forEach((element) => {
      element.classList.toggle('disabled', disable)
    })
  }

  renderPage(searchParams, event, updateURLHash = true) {
    const productGrid = document.querySelector(this.selectors.productGrid)
    const productGridInner = productGrid?.querySelector(
      this.selectors.productGridInner
    )
    productGridInner?.classList.add('loading')

    const url = this.buildSectionRenderingUrl(searchParams)
    const filterDataUrl = (element) => element.url === url

    this.filterData.some(filterDataUrl)
      ? this.renderSectionFromCache(filterDataUrl, event)
      : this.renderSectionFromFetch(url, event)

    if (updateURLHash) this.updateURLHash(searchParams)
  }

  buildSectionRenderingUrl(searchParams) {
    return (
      `${window.location.pathname}?` +
      this.getSections()
        .map((section) => `sections[]=${section.section}`)
        .join('&') +
      `&${searchParams}`
    )
  }

  renderPageForVisualFilter(collectionUrl, event) {
    const productGrid = document.querySelector(this.selectors.productGrid)
    const productGridInner = productGrid?.querySelector(
      this.selectors.productGridInner
    )
    productGridInner?.classList.add('loading')

    // Build URL for Section Rendering API to fetch new collection's sections
    const url =
      `${collectionUrl}?` +
      this.getSections()
        .map((section) => `sections[]=${section.section}`)
        .join('&')

    // Mark this as a visual filter change by creating a special event object
    const visualFilterEvent = {
      ...event,
      isVisualFilterChange: true,
      target: null, // Ensure no target to trigger visual filter logic
    }

    // For visual filter changes, always fetch fresh data to ensure correct filter structure
    // Don't use cache as it might contain filters from a different collection
    this.renderSectionFromFetch(url, visualFilterEvent)

    // Update URL without search params since this is a collection change
    this.updateURLHash('')
  }

  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.json())
      .then((parsed) => {
        const html = Object.values(parsed).join('')
        this.filterData = [...this.filterData, { html, url }]
        this.renderFilters(html, event)
        this.renderProductGrid(html)
      })
  }

  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html
    this.renderFilters(html, event)
    this.renderProductGrid(html)
  }

  renderProductGrid(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const newProductGrid = parsedHTML.getElementById('CollectionProductGrid')
    const currentProductGrid = document.querySelector(
      this.selectors.productGrid
    )

    if (newProductGrid && currentProductGrid) {
      currentProductGrid.innerHTML = newProductGrid.innerHTML
    }
  }

  renderFilters(...args) {
    this.renderSingleFormFilters(this.formIds.desktop, ...args)
    this.renderSingleFormFilters(this.formIds.mobile, ...args)
  }

  renderSingleFormFilters(form, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    // Check if this is a visual filter change (no event target means collection change)
    const isVisualFilterChange = event?.isVisualFilterChange

    if (isVisualFilterChange) {
      this.replaceFiltersContainer(form, parsedHTML)
    } else {
      this.updateIndividualFilters(form, parsedHTML, event)
    }

    this.renderActiveFacets(parsedHTML)
    this.initializeDropdownComponents()
  }

  replaceFiltersContainer(form, parsedHTML) {
    const newFiltersContainer = parsedHTML.querySelector(
      `${form} ${this.selectors.filtersContainer}`
    )
    const currentFiltersContainer = document.querySelector(
      `${form} ${this.selectors.filtersContainer}`
    )

    if (newFiltersContainer && currentFiltersContainer) {
      currentFiltersContainer.innerHTML = newFiltersContainer.innerHTML
    }
  }

  updateIndividualFilters(form, parsedHTML, event) {
    const facetDetailsElements = parsedHTML.querySelectorAll(
      `${form} ${this.selectors.filters}`
    )
    const matchesIndex = (element) =>
      element.dataset.index ===
      event?.target.closest(this.selectors.filters)?.dataset.index

    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    )

    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex)

    facetsToRender.forEach((element) => {
      const filterElement = document.querySelector(
        `${form} ${this.selectors.filters}[data-index="${element.dataset.index}"]`
      )
      if (filterElement) {
        filterElement.innerHTML = element.innerHTML
      }
    })

    if (countsToRender) {
      this.renderCounts(
        countsToRender,
        event.target.closest(this.selectors.filters)
      )
    }
  }

  initializeDropdownComponents() {
    this.querySelectorAll(this.selectors.dropdownDisclosure).forEach(
      (dropdownDisclosure) => {
        dropdownDisclosure.init()
      }
    )
  }

  renderActiveFacets(html) {
    const activeFacetsElement = html.querySelector(this.selectors.activeFacets)
    if (!activeFacetsElement) return

    const currentActiveFacets = document.querySelector(
      this.selectors.activeFacets
    )
    if (currentActiveFacets) {
      currentActiveFacets.innerHTML = activeFacetsElement.innerHTML
    }

    this.bindActiveFacetButtonEvents()
    this.toggleActiveFacets(false)
    this.checkInputOnEnter()
    this.checkInputOnEnterSort()
  }

  renderCounts(source, target) {
    const countElementSelectors = [
      this.selectors.bubble,
      this.selectors.selected,
    ]
    countElementSelectors.forEach((selector) => {
      const targetElement = target.querySelector(selector)
      const sourceElement = source.querySelector(selector)

      if (sourceElement && targetElement) {
        target.querySelector(selector).outerHTML =
          source.querySelector(selector).outerHTML
      }
    })
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll(this.selectors.facetRemove).forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick, {
        once: true,
      })
    })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    )
  }

  checkInputOnEnter() {
    const checkboxes = document.querySelectorAll(this.selectors.filterCheckbox)
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('keypress', (event) => {
        if (event.keyCode == 13) {
          event.target.click()
        }
      })
    })
  }

  checkInputOnEnterSort() {
    const checkboxes = document.querySelectorAll(this.selectors.sortCheckbox)
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('keypress', (event) => {
        if (event.key === 'Enter') {
          const input = checkbox.querySelector('input[type="radio"]')
          input.checked = !input.checked
          input.click()
        }
      })
    })
  }

  getSections() {
    return [
      {
        id: 'main-collection-filters',
        section: document.getElementById('main-collection-filters').dataset.id,
      },
      {
        id: 'main-collection-product-grid',
        section: document.getElementById('main-collection-product-grid').dataset
          .id,
      },
    ]
  }
}

customElements.define('hh-filters-wrapper', HHFiltersWrapper)
